import type { Locale } from '@/lib/i18n';

const numberFormatters = new Map<string, Intl.NumberFormat>();
const consentDateFormatters = new Map<string, Intl.DateTimeFormat>();
const declarationDateFormatters = new Map<string, Intl.DateTimeFormat>();
const RIGHT_TO_LEFT_MARK = '\u200f';

function getNumberFormatter(locale: Locale): Intl.NumberFormat {
  const key = locale;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      // Western digits for Arabic — data consistency across locales (task 17).
      numberingSystem: locale === 'ar' ? 'latn' : undefined,
    });
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

function getConsentDateFormatter(locale: Locale): Intl.DateTimeFormat {
  let formatter = consentDateFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      hour12: true,
      numberingSystem: locale === 'ar' ? 'latn' : undefined,
    });
    consentDateFormatters.set(locale, formatter);
  }
  return formatter;
}

function getDeclarationDateFormatter(locale: Locale): Intl.DateTimeFormat {
  let formatter = declarationDateFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      numberingSystem: locale === 'ar' ? 'latn' : undefined,
    });
    declarationDateFormatters.set(locale, formatter);
  }
  return formatter;
}

/** Locale-aware integer formatting (pressure card counts, etc.). */
export function formatNumber(locale: Locale, value: number): string {
  return getNumberFormatter(locale).format(value);
}

/**
 * Locale-aware whole percentage. Arabic keeps the logical number-first source
 * while the directional mark places the percent sign after it in RTL reading
 * order (visually to the number's left).
 */
export function formatPercentage(locale: Locale, value: number): string {
  const number = formatNumber(locale, Math.round(value));
  return locale === 'ar' ? `${number}${RIGHT_TO_LEFT_MARK}%` : `${number}%`;
}

/** Consent decision timestamp for Cookie Settings (weekday + date + time + TZ). */
export function formatConsentDate(locale: Locale, iso: string): string {
  return getConsentDateFormatter(locale).format(new Date(iso));
}

/** “Cookie declaration last updated” calendar date (e.g. 28/07/2026). */
export function formatDeclarationDate(locale: Locale, date: Date = new Date()): string {
  return getDeclarationDateFormatter(locale).format(date);
}
